import { useState, useEffect, useMemo, useRef, useCallback, type SetStateAction, ReactElement } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../services/api";
import { TransactionCategory, TransactionCategoryDetail, TransactionCategoryGroup } from "../types";
import { useCategories } from "./CategorySettings/hooks/useCategories";
import { useCategoryGroups } from "./CategorySettings/hooks/useCategoryGroups";
import { useCategoryTransactions } from "./CategorySettings/hooks/useCategoryTransactions";
import { useCategoryUIState } from "./CategorySettings/hooks/useCategoryUIState";
import { useCategoryValidation } from "./CategorySettings/hooks/useCategoryValidation";
import { useCategoryHelpers } from "./CategorySettings/hooks/useCategoryHelpers";
import { useCategoryHandlers } from "./CategorySettings/hooks/useCategoryHandlers";
import { useCategoryGroupHandlers } from "./CategorySettings/hooks/useCategoryGroupHandlers";
import { useCategoryDetailHandlers } from "./CategorySettings/hooks/useCategoryDetailHandlers";
import { useCategoryDragAndDrop } from "./CategorySettings/hooks/useCategoryDragAndDrop";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Stack,
  IconButton,
  Tabs,
  Tab,
  Alert,
  Chip,
  Tooltip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import StandardDialog from "../components/StandardDialog";
import LoadingSpinner from "../components/LoadingSpinner";
import ListItemAutocomplete from "../components/ListItemAutocomplete";
import DeleteIconButton from "../components/DeleteIconButton";
import CannotChangeLaterIcon from "../components/CannotChangeLaterIcon";
import CategoryDialog from "./CategorySettings/components/CategoryDialog";
import CategoryGroupDialog from "./CategorySettings/components/CategoryGroupDialog";
import SelectDetailsDialog from "./CategorySettings/components/SelectDetailsDialog";
import GroupDetailsDialog, { SortableDetailCard } from "./CategorySettings/components/GroupDetailsDialog";
import SortableCategoryCard from "./CategorySettings/components/SortableCategoryCard";
import SortableGroupCard from "./CategorySettings/components/SortableGroupCard";
import { isDemoMode } from "../utils/demoMode";
import { DemoModeMessage } from "../utils/demoModeMessage";
import { Transaction } from "../types";

interface CategorySettingsProps {
  workspaceId: string;
}


function CategorySettings({ workspaceId }: CategorySettingsProps): ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  const [selectedTab, setSelectedTab] = useState<"expense" | "income">("expense");

  // Preload both expense and income data so tab switch is instant (no late load)
  const { categories: categoriesExpense, loading: loadingExpense, error: errorExpense, setCategories: setCategoriesExpense } = useCategories({
    workspaceId,
    type: "expense",
  });
  const { categories: categoriesIncome, loading: loadingIncome, error: errorIncome, setCategories: setCategoriesIncome } = useCategories({
    workspaceId,
    type: "income",
  });
  const { groups: groupsExpense, loading: loadingGroupsExpense, setGroups: setGroupsExpense } = useCategoryGroups({
    workspaceId,
    type: "expense",
  });
  const { groups: groupsIncome, loading: loadingGroupsIncome, setGroups: setGroupsIncome } = useCategoryGroups({
    workspaceId,
    type: "income",
  });

  const categories = selectedTab === "expense" ? categoriesExpense : categoriesIncome;
  const groups = selectedTab === "expense" ? groupsExpense : groupsIncome;
  // Show loading until categories and groups (both tabs) are loaded so content shows when entering
  const loading = loadingExpense || loadingIncome || loadingGroupsExpense || loadingGroupsIncome;
  const categoriesError = selectedTab === "expense" ? errorExpense : errorIncome;
  const setCategories = useCallback(
    (arg: SetStateAction<TransactionCategory[]>) => {
      if (selectedTab === "expense") setCategoriesExpense(arg);
      else setCategoriesIncome(arg);
    },
    [selectedTab]
  );
  const setGroups = useCallback(
    (arg: SetStateAction<TransactionCategoryGroup[]>) => {
      if (selectedTab === "expense") setGroupsExpense(arg);
      else setGroupsIncome(arg);
    },
    [selectedTab]
  );

  const { transactions, loading: transactionsLoading } = useCategoryTransactions({
    workspaceId,
    type: selectedTab,
  });
  // UI state management hook
  const uiState = useCategoryUIState();
  
  // Destructure UI state for easier access
  const {
    // Dialog states
    addDialogOpen,
    setAddDialogOpen,
    addGroupDialogOpen,
    setAddGroupDialogOpen,
    editDialogOpen,
    setEditDialogOpen,
    detailsDialogOpen,
    setDetailsDialogOpen,
    deleteDialogOpen,
    setDeleteDialogOpen,
    groupDeleteDialogOpen,
    setGroupDeleteDialogOpen,
    editGroupDialogOpen,
    setEditGroupDialogOpen,
    groupDetailsDialogOpen,
    setGroupDetailsDialogOpen,
    selectDetailsDialogOpen,
    setSelectDetailsDialogOpen,
    // Editing states
    editingCategory,
    setEditingCategory,
    categoryForDetails,
    setCategoryForDetails,
    categoryToDelete,
    setCategoryToDelete,
    groupToDelete,
    setGroupToDelete,
    editingGroup,
    setEditingGroup,
    categoryForSelectDetails,
    setCategoryForSelectDetails,
    // Form state
    categoryName,
    setCategoryName,
    categoryGroupId,
    setCategoryGroupId,
    groupInputValue,
    setGroupInputValue,
    newGroupName,
    setNewGroupName,
    editGroupName,
    setEditGroupName,
    saving,
    setSaving,
    creatingGroup,
    setCreatingGroup,
    error,
    setError,
    categoryNameError,
    setCategoryNameError,
    categoryNameWarning,
    setCategoryNameWarning,
    groupNameError,
    setGroupNameError,
    // Group details management state
    editingGroupDetails,
    setEditingGroupDetails,
    originalGroupDetails,
    setOriginalGroupDetails,
    editingGroupNameForDetails,
    setEditingGroupNameForDetails,
    // Details management state (legacy)
    editingDetails,
    setEditingDetails,
    originalDetails,
    setOriginalDetails,
    newDetailName,
    setNewDetailName,
    newDetailType,
    setNewDetailType,
    newDetailUnit,
    setNewDetailUnit,
    newListItems,
    setNewListItems,
    editingDetailIndex,
    setEditingDetailIndex,
    editingOriginalDetail,
    setEditingOriginalDetail,
    detailNameError,
    setDetailNameError,
    savingDetails,
    setSavingDetails,
    // Category detail selection state
    enabledDetails,
    setEnabledDetails,
    defaultValues,
    setDefaultValues,
    originalEnabledDetails,
    setOriginalEnabledDetails,
    originalDefaultValues,
    setOriginalDefaultValues,
    // Filter state
    searchTerm,
    setSearchTerm,
    groupSearchTerm,
    setGroupSearchTerm,
    // Accordion expansion state
    groupsAccordionExpanded,
    setGroupsAccordionExpanded,
    categoriesAccordionExpanded,
    setCategoriesAccordionExpanded,
    expandedGroupIds,
    setExpandedGroupIds,
  } = uiState;

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Reset filters when tab changes
  useEffect(() => {
    setSearchTerm("");
    setCategorySearchOpen(false);
    setGroupSearchOpen(false);
    setGroupSearchTerm("");
  }, [selectedTab]);

  // Initialize expanded groups when groups are loaded
  useEffect(() => {
    if (groups.length > 0) {
      setExpandedGroupIds(new Set(groups.map(g => g.id)));
    }
  }, [groups.length]);

  // Update error state when categoriesError changes
  useEffect(() => {
    if (categoriesError) {
      setError(categoriesError);
    }
  }, [categoriesError]);

  // Validation hook
  const { checkCategoryDuplicate } = useCategoryValidation({
    categories,
    groups,
    setCategoryNameError,
    setCategoryNameWarning,
  });

  // Helpers hook
  const {
    groupedCategories,
    isCategoryUsed,
    isDetailUsed,
    isGroupUsed,
    isGroupDetailUsed,
    countCategoriesInGroup,
    countTransactionsInGroup,
    hasDetailsChanged: hasDetailsChangedHelper,
    hasGroupDetailsChanged,
    hasPartialFormData,
    hasEditedDetailChanged,
    hasDetailSelectionChanged,
  } = useCategoryHelpers({
    categories,
    groups,
    transactions,
  });

  // Category handlers hook
  const categoryHandlers = useCategoryHandlers({
    workspaceId,
    selectedTab,
    categories,
    groups,
    setCategories,
    setGroups,
    setCategoryName,
    setCategoryGroupId,
    setGroupInputValue,
    setError,
    setCategoryNameError,
    setCategoryNameWarning,
    setSaving,
    setAddDialogOpen,
    setEditDialogOpen,
    setDeleteDialogOpen,
    setSelectDetailsDialogOpen,
    setEditingCategory,
    setCategoryToDelete,
    setCategoryForSelectDetails,
    setEnabledDetails,
    setDefaultValues,
    setOriginalEnabledDetails,
    setOriginalDefaultValues,
    categoryName,
    categoryGroupId,
    groupInputValue,
    editingCategory,
    categoryToDelete,
  });

  // Group handlers hook
  const groupHandlers = useCategoryGroupHandlers({
    workspaceId,
    selectedTab,
    groups,
    categories,
    setGroups,
    setCategories,
    setError,
    setSaving,
    setCreatingGroup,
    setEditGroupDialogOpen,
    setGroupDeleteDialogOpen,
    setGroupDetailsDialogOpen,
    setEditingGroup,
    setGroupToDelete,
    setEditGroupName,
    setEditingGroupDetails,
    setOriginalGroupDetails,
    setEditingGroupNameForDetails,
    setNewDetailName,
    setNewDetailType,
    setNewDetailUnit,
    setNewListItems,
    setDetailNameError,
    setEditingDetailIndex,
    setEditingOriginalDetail,
    isGroupUsed,
    countCategoriesInGroup,
    creatingGroup,
    saving,
    editingGroup,
    editGroupName,
    groupToDelete,
  });

  // Detail handlers hook
  const detailHandlers = useCategoryDetailHandlers({
    workspaceId,
    selectedTab,
    categories,
    groups,
    setCategories,
    setGroups,
    setError,
    setSavingDetails,
    setSelectDetailsDialogOpen,
    setGroupDetailsDialogOpen,
    setCategoryForSelectDetails,
    setEnabledDetails,
    setDefaultValues,
    setOriginalEnabledDetails,
    setOriginalDefaultValues,
    setEditingGroupDetails,
    setOriginalGroupDetails,
    setEditingGroupNameForDetails,
    setNewDetailName,
    setNewDetailType,
    setNewDetailUnit,
    setNewListItems,
    setDetailNameError,
    setEditingDetailIndex,
    setEditingOriginalDetail,
    setEditingDetails,
    categoryForSelectDetails,
    enabledDetails,
    defaultValues,
    editingGroupDetails,
    editingGroupNameForDetails,
    editingDetails,
    editingDetailIndex,
  });

  // On mobile, navigate to full-page Manage/Select Details instead of opening dialogs
  const handleGroupDetailsForCard = useCallback(
    (group: TransactionCategoryGroup) => {
      if (isMobile && workspaceSlug) {
        navigate(`/${workspaceSlug}/settings/manage-details/${selectedTab}/${group.id}`);
      } else {
        groupHandlers.handleGroupDetails(group);
      }
    },
    [isMobile, workspaceSlug, selectedTab, navigate, groupHandlers]
  );

  const handleDetailsCategoryForCard = useCallback(
    (category: TransactionCategory) => {
      if (isMobile && workspaceSlug) {
        navigate(`/${workspaceSlug}/settings/select-details/${selectedTab}/${category.id}`);
      } else {
        categoryHandlers.handleDetailsCategory(category);
      }
    },
    [isMobile, workspaceSlug, selectedTab, navigate, categoryHandlers]
  );

  // Drag and drop hook
  const dragAndDropHandlers = useCategoryDragAndDrop({
    workspaceId,
    selectedTab,
    categories,
    groups,
    groupedCategories,
    setCategories,
    setGroups,
    setError,
  });

  // Refs and state for auto-scroll during group/category reorder (same as account group ordering)
  const groupListScrollRef = useRef<HTMLDivElement>(null);
  const categoriesScrollRef = useRef<HTMLDivElement>(null);
  const [isDraggingGroup, setIsDraggingGroup] = useState(false);
  const [isDraggingCategory, setIsDraggingCategory] = useState(false);
  const [groupSearchOpen, setGroupSearchOpen] = useState(false);
  const [categorySearchOpen, setCategorySearchOpen] = useState(false);

  const handleGroupDragEnd = useCallback(
    (event: Parameters<typeof dragAndDropHandlers.handleGroupDragEnd>[0]) => {
      setIsDraggingGroup(false);
      dragAndDropHandlers.handleGroupDragEnd(event);
    },
    [dragAndDropHandlers]
  );

  // Auto-scroll when dragging group near top/bottom
  useEffect(() => {
    if (!isDraggingGroup) return;
    const container = groupListScrollRef.current;
    if (!container) return;
    const EDGE_ZONE = 0.25;
    const SCROLL_STEP = 12;
    const handlePointerMove = (e: PointerEvent) => {
      const rect = container.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const relativeY = y / rect.height;
      if (relativeY < EDGE_ZONE) container.scrollTop -= SCROLL_STEP;
      else if (relativeY > 1 - EDGE_ZONE) container.scrollTop += SCROLL_STEP;
    };
    document.addEventListener("pointermove", handlePointerMove, { passive: true });
    return () => document.removeEventListener("pointermove", handlePointerMove);
  }, [isDraggingGroup]);

  // Auto-scroll when dragging category near top/bottom
  useEffect(() => {
    if (!isDraggingCategory) return;
    const container = categoriesScrollRef.current;
    if (!container) return;
    const EDGE_ZONE = 0.25;
    const SCROLL_STEP = 12;
    const handlePointerMove = (e: PointerEvent) => {
      const rect = container.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const relativeY = y / rect.height;
      if (relativeY < EDGE_ZONE) container.scrollTop -= SCROLL_STEP;
      else if (relativeY > 1 - EDGE_ZONE) container.scrollTop += SCROLL_STEP;
    };
    document.addEventListener("pointermove", handlePointerMove, { passive: true });
    return () => document.removeEventListener("pointermove", handlePointerMove);
  }, [isDraggingCategory]);

  // Expense = red, Income = green so users can tell context at a glance
  const accentColor = selectedTab === "expense" ? "error" : "success";

  if (isDemoMode()) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          <DemoModeMessage message="Demo mode: Categories cannot be managed. Please log in to manage categories." />
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Tabs
        value={selectedTab === "expense" ? 0 : 1}
        onChange={(_, newValue) => setSelectedTab(newValue === 0 ? "expense" : "income")}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          marginBottom: 1.25,
          minHeight: isMobile ? 40 : 48,
          "& .MuiTab-root": { minHeight: isMobile ? 40 : 48, py: isMobile ? 1 : 1.5 },
          "& .MuiTab-root.Mui-selected": { color: accentColor === "error" ? "error.main" : "success.main" },
          "& .MuiTabs-indicator": { backgroundColor: accentColor === "error" ? "error.main" : "success.main" },
        }}
      >
        <Tab label="Expenses" />
        <Tab label="Income" />
      </Tabs>

      <Box sx={{ flex: 1, p: 0, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
        {/* Category Groups Section - Top */}
        <Accordion 
          expanded={groupsAccordionExpanded} 
          onChange={(_, isExpanded) => {
            setGroupsAccordionExpanded(isExpanded);
            if (!isExpanded) setCategoriesAccordionExpanded(true);
          }}
          disableGutters
          sx={{
            marginBottom: 1.25,
            flexShrink: 0,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            "& .MuiAccordionSummary-root": { minHeight: isMobile ? 40 : 48, py: 0 },
            "&:before": { display: "none" },
            boxShadow: "none",
          }}
        >
          <AccordionSummary component="div" expandIcon={<ExpandMoreIcon />}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                pr: 0.5,
                gap: 1,
              }}
            >
              <Typography variant="h6" sx={{ flex: 1, minWidth: 0, fontSize: isMobile ? "1rem" : undefined }}>Category Groups</Typography>
              {groupSearchOpen ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                  <TextField
                    size="small"
                    placeholder="Search groups..."
                    value={groupSearchTerm}
                    onChange={(e) => setGroupSearchTerm(e.target.value)}
                    autoFocus
                    sx={{ width: 200, maxWidth: 300 }}
                  />
                  <IconButton size="small" onClick={() => { setGroupSearchOpen(false); setGroupSearchTerm(""); }} aria-label="Close search">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Tooltip title="Search groups">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); setGroupSearchOpen(true); if (!groupsAccordionExpanded) setGroupsAccordionExpanded(true); }} aria-label="Search groups">
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Create Group">
                <IconButton
                  size="small"
                  color={accentColor}
                  onClick={(e) => {
                    e.stopPropagation();
                    setNewGroupName("");
                    setGroupNameError("");
                    setAddGroupDialogOpen(true);
                  }}
                  aria-label="Create group"
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ flexDirection: "column", display: "flex", p: isMobile ? 1.5 : 2, pt: 0 }}>
            {(() => {
              const filteredGroups = groups.filter((group) => {
                if (groupSearchTerm.trim()) {
                  const searchLower = groupSearchTerm.toLowerCase();
                  const groupNameLower = group.name.toLowerCase();
                  return groupNameLower.includes(searchLower);
                }
                return true;
              });

              if (filteredGroups.length === 0) {
                if (groups.length === 0) {
                  return (
                    <Box sx={{ p: isMobile ? 2 : 3, textAlign: "center" }}>
                      <Typography variant="body1" color="text.secondary" gutterBottom>
                        No category groups configured
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Use the button above to create your first group
                      </Typography>
                    </Box>
                  );
                } else {
                  return (
                    <Box sx={{ p: isMobile ? 2 : 3, textAlign: "center" }}>
                      <Typography variant="body1" color="text.secondary">
                        No groups found matching your search.
                      </Typography>
                    </Box>
                  );
                }
              }

              const needsScroll = filteredGroups.length > 4;
              const maxHeight = needsScroll
                ? (isMobile ? "min(40vh, 220px)" : "215px")
                : "none";

              return (
                <Box
                  ref={groupListScrollRef}
                  sx={{
                    maxHeight,
                    overflowY: needsScroll ? "auto" : "visible",
                    overflowX: "hidden",
                    WebkitOverflowScrolling: "touch",
                    pr: needsScroll ? 0.5 : 0,
                  }}
                >
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={() => setIsDraggingGroup(true)}
                    onDragEnd={handleGroupDragEnd}
                    onDragCancel={() => setIsDraggingGroup(false)}
                  >
                    <SortableContext
                      items={filteredGroups.map((g) => g.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <Stack spacing={0.5}>
                        {filteredGroups.map((group) => {
                          const inUse = isGroupUsed(group.id);
                          const categoriesUsingGroup = categories.filter((cat) => cat.groupId === group.id);

                          return (
                        <SortableGroupCard
                          key={group.id}
                          group={group}
                          inUse={inUse}
                          categoriesUsingGroup={categoriesUsingGroup}
                          onEdit={groupHandlers.handleEditGroup}
                          onDelete={groupHandlers.handleDeleteGroup}
                          onDetails={handleGroupDetailsForCard}
                        />
                          );
                        })}
                      </Stack>
                    </SortableContext>
                  </DndContext>
                </Box>
              );
            })()}
          </AccordionDetails>
        </Accordion>

        {/* Categories Section - Bottom */}
        <Accordion
          expanded={categoriesAccordionExpanded}
          onChange={(_, isExpanded) => {
            setCategoriesAccordionExpanded(isExpanded);
            if (!isExpanded) setGroupsAccordionExpanded(true);
          }}
          disableGutters
          sx={{
            marginBottom: 1.25,
            flexShrink: 0,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            "& .MuiAccordionSummary-root": { minHeight: isMobile ? 40 : 48, py: 0 },
            "&:before": { display: "none" },
            boxShadow: "none",
          }}
        >
          <AccordionSummary component="div" expandIcon={<ExpandMoreIcon />}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                pr: 0.5,
                gap: 1,
              }}
            >
              <Typography variant="h6" sx={{ flex: 1, minWidth: 0, fontSize: isMobile ? "1rem" : undefined }}>Categories</Typography>
              {categorySearchOpen ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                  <TextField
                    size="small"
                    placeholder="Search categories..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                    sx={{ width: 200, maxWidth: 300 }}
                  />
                  <IconButton size="small" onClick={() => { setCategorySearchOpen(false); setSearchTerm(""); }} aria-label="Close search">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Tooltip title="Search categories">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); setCategorySearchOpen(true); if (!categoriesAccordionExpanded) setCategoriesAccordionExpanded(true); }} aria-label="Search categories">
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Add Category">
                <IconButton
                  size="small"
                  color={accentColor}
                  onClick={(e) => {
                    e.stopPropagation();
                    categoryHandlers.handleAddCategory();
                  }}
                  aria-label="Add category"
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ flexDirection: "column", display: "flex", p: isMobile ? 1.5 : 2, pt: 0 }}>
            {loading ? (
              <LoadingSpinner />
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : (
              <Box
                ref={categoriesScrollRef}
                sx={{
                  maxHeight: groupsAccordionExpanded
                    ? (isMobile ? "min(55vh, 380px)" : "420px")
                    : (isMobile ? "min(78vh, 520px)" : "min(72vh, 620px)"),
                  overflowY: "auto",
                  overflowX: "hidden",
                  WebkitOverflowScrolling: "touch",
                  pr: 0.5,
                }}
              >
                  {(() => {
                    // Filter and group categories
                    const filteredGroupedCategories: Record<string, TransactionCategory[]> = {};

                    groups.forEach((group) => {
                      const groupCategories = (groupedCategories[group.id] || []).filter((category) => {
                        if (searchTerm.trim()) {
                          const searchLower = searchTerm.toLowerCase();
                          const categoryNameLower = category.name.toLowerCase();
                          if (!categoryNameLower.includes(searchLower)) return false;
                        }
                        return true;
                      });

                      if (groupCategories.length > 0) {
                        filteredGroupedCategories[group.id] = groupCategories;
                      }
                    });

                    const totalFilteredCategories = Object.values(filteredGroupedCategories).reduce((sum, cats) => sum + cats.length, 0);

                    if (totalFilteredCategories === 0) {
                      if (categories.length === 0) {
                        return (
                          <Box sx={{ p: isMobile ? 2 : 3, textAlign: "center" }}>
                            <Typography variant="body1" color="text.secondary" gutterBottom>
                              No {selectedTab === "expense" ? "expense" : "income"} categories yet.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Click "Add Category" to create your first one.
                            </Typography>
                          </Box>
                        );
                      } else {
                        return (
                          <Box sx={{ p: isMobile ? 2 : 3, textAlign: "center" }}>
                            <Typography variant="body1" color="text.secondary">
                              No categories found matching your filters.
                            </Typography>
                          </Box>
                        );
                      }
                    }

                    return (
                      <Stack spacing={1.5}>
                        {groups.map((group) => {
                          const groupCategories = filteredGroupedCategories[group.id];
                          if (!groupCategories || groupCategories.length === 0) return null;

                          return (
                            <Accordion 
                              key={group.id} 
                              expanded={expandedGroupIds.has(group.id)}
                              onChange={(_, isExpanded) => {
                                const newExpanded = new Set(expandedGroupIds);
                                if (isExpanded) {
                                  newExpanded.add(group.id);
                                } else {
                                  newExpanded.delete(group.id);
                                }
                                setExpandedGroupIds(newExpanded);
                              }}
                              disableGutters
                              sx={{
                                "&:before": { display: "none" },
                                marginTop: 0,
                                marginBottom: 0,
                                "& .MuiAccordionSummary-root": { minHeight: isMobile ? 24 : 28, py: 0 },
                                "& .MuiAccordionDetails-root": { pt: 0 },
                              }}
                            >
                              <AccordionSummary component="div" expandIcon={<ExpandMoreIcon fontSize="small" />}>
                                <Box 
                                  sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 1, gap: 1.5 }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Typography variant="subtitle2" fontWeight="medium" sx={{ fontSize: isMobile ? "0.8125rem" : undefined, flex: 1, minWidth: 0 }}>
                                    {group.name} ({groupCategories.length})
                                  </Typography>
                                  <Tooltip title="Add category in this group">
                                    <IconButton
                                      size="small"
                                      color={accentColor}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        categoryHandlers.handleAddCategoryInGroup(group);
                                      }}
                                      aria-label={`Add category to ${group.name}`}
                                      sx={{ p: 0.25 }}
                                    >
                                      <AddIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails sx={{ px: 0, py: isMobile ? 0.25 : 0.5, pb: 1.5 }}>
                                <DndContext
                                  sensors={sensors}
                                  collisionDetection={closestCenter}
                                  onDragStart={() => setIsDraggingCategory(true)}
                                  onDragEnd={(e) => {
                                    setIsDraggingCategory(false);
                                    dragAndDropHandlers.handleCategoryDragEnd(e, group.id);
                                  }}
                                  onDragCancel={() => setIsDraggingCategory(false)}
                                >
                                  <SortableContext
                                    items={groupCategories.map((cat) => cat.id)}
                                    strategy={verticalListSortingStrategy}
                                  >
                                    <Stack spacing={isMobile ? 0.25 : 0.35}>
                                      {groupCategories.map((category) => (
                                        <SortableCategoryCard
                                          key={category.id}
                                          category={category}
                                          onEdit={categoryHandlers.handleEditCategory}
                                          onDelete={categoryHandlers.handleDeleteCategory}
                                          onDetails={handleDetailsCategoryForCard}
                                          isCategoryUsed={isCategoryUsed}
                                          enableOrdering={true}
                                          groups={groups}
                                        />
                                      ))}
                                    </Stack>
                                  </SortableContext>
                                </DndContext>
                              </AccordionDetails>
                            </Accordion>
                          );
                        })}
                      </Stack>
                    );
                  })()}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Add/Edit Category Dialog */}
      <CategoryDialog
        open={addDialogOpen || editDialogOpen}
        onClose={() => {
          if (!saving) {
            setCategoryNameWarning("");
            setCategoryNameError("");
            if (addDialogOpen) {
            setAddDialogOpen(false);
          }
            if (editDialogOpen) {
              setEditDialogOpen(false);
            }
          }
        }}
        onConfirm={categoryHandlers.handleSaveCategory}
        selectedTab={selectedTab}
        editingCategory={editingCategory}
        groups={groups}
        categoryName={categoryName}
        setCategoryName={setCategoryName}
        categoryGroupId={categoryGroupId}
        setCategoryGroupId={setCategoryGroupId}
        groupInputValue={groupInputValue}
        setGroupInputValue={setGroupInputValue}
        categoryNameError={categoryNameError}
        categoryNameWarning={categoryNameWarning}
        error={error}
        saving={saving}
        checkCategoryDuplicate={checkCategoryDuplicate}
      />

      {/* Details Management Dialog */}
      {categoryForDetails && (
        <StandardDialog
          open={detailsDialogOpen}
          onClose={() => {
            if (!savingDetails) {
              setDetailsDialogOpen(false);
              setEditingDetails([]);
              setOriginalDetails([]);
              setNewDetailName("");
              setNewDetailType("");
              setNewDetailUnit("");
              setNewListItems([]);
              setDetailNameError("");
              setEditingDetailIndex(null);
              setEditingOriginalDetail(null);
            }
          }}
          title={`Manage Details - ${categoryForDetails.name}`}
          confirmText={savingDetails ? "Saving..." : "Save"}
          onConfirm={async () => {
            setSavingDetails(true);
            setError("");

            try {
              if (!categoryForDetails) return;

              await api.updateCategory(workspaceId, categoryForDetails.id, undefined, undefined, editingDetails);
              setDetailsDialogOpen(false);
              // Refresh categories
              const data = await api.getCategories(workspaceId, selectedTab);
              setCategories(data);
              setEditingDetails([]);
              setOriginalDetails([]);
              setNewDetailName("");
              setNewDetailType("");
              setNewDetailUnit("");
              setNewListItems([]);
              setDetailNameError("");
              setEditingDetailIndex(null);
              setEditingOriginalDetail(null);
            } catch (err: any) {
              setError(err.message || "Failed to save details");
            } finally {
              setSavingDetails(false);
            }
          }}
          confirmDisabled={savingDetails || !hasDetailsChangedHelper(editingDetails, originalDetails) || editingDetailIndex !== null || hasPartialFormData(newDetailName, newDetailType, newDetailUnit, newListItems, editingDetailIndex)}
          maxWidth="sm"
          alert={error ? { severity: "error", message: error } : undefined}
        >
          <Box>
            {/* Add/Edit Detail - Always at top */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                {editingDetailIndex !== null ? "Edit Detail" : "Add New Detail"}
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Detail Name"
                  value={newDetailName}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewDetailName(value);
                    // Check for duplicate names (excluding the one being edited)
                    const trimmedName = value.trim();
                    if (trimmedName) {
                      const duplicate = editingDetails.some(
                        (d, i) => i !== editingDetailIndex && d.name.toLowerCase() === trimmedName.toLowerCase()
                      );
                      if (duplicate) {
                        setDetailNameError("A detail with this name already exists.");
                      } else {
                        setDetailNameError("");
                      }
                    } else {
                      setDetailNameError("");
                    }
                  }}
                  fullWidth
                  placeholder="e.g., Location, Car, Odometer"
                  error={!!detailNameError}
                  helperText={detailNameError}
                  disabled={savingDetails}
                  sx={{
                    "& .MuiFormHelperText-root": {
                      fontSize: "0.75rem",
                    }
                  }}
                />
                  <FormControl fullWidth sx={{ position: "relative" }}>
                    <InputLabel>Detail Type</InputLabel>
                    <Select
                      value={newDetailType}
                      onChange={(e) => {
                        const newType = e.target.value as "list" | "measurable";
                        setNewDetailType(newType);
                        if (newType !== "list") {
                          setNewListItems([]);
                        }
                        if (newType !== "measurable") {
                          setNewDetailUnit("");
                        }
                      }}
                      label="Detail Type"
                      disabled={savingDetails || editingDetailIndex !== null} // Type can't be changed when editing
                      MenuProps={{
                        MenuListProps: {
                          onWheel: (e: React.WheelEvent) => {
                            const listbox = e.currentTarget;
                            if (listbox.scrollHeight <= listbox.clientHeight) return;
                            e.stopPropagation();
                            e.preventDefault();
                            listbox.scrollTop += e.deltaY;
                          },
                        },
                      }}
                    >
                      <MenuItem value="">Select type...</MenuItem>
                      <MenuItem value="list">List (Selectable values)</MenuItem>
                      <MenuItem value="measurable">Measurable (Unit)</MenuItem>
                    </Select>
                    {editingDetailIndex === null && editingOriginalDetail === null && (
                      <CannotChangeLaterIcon
                        tooltipMessage="Detail type cannot be changed after creation"
                      />
                    )}
                  </FormControl>
                {/* List Items Management - Show when type is "list" */}
                {newDetailType === "list" && (
                  <ListItemAutocomplete
                    items={newListItems}
                    onChange={setNewListItems}
                    label="List Items"
                    placeholder="Type to add items (e.g., Audi TT, BMW E92)"
                    detailName={
                      editingDetailIndex !== null && editingOriginalDetail
                        ? editingOriginalDetail.name
                        : undefined
                    }
                    categoryName={categoryForDetails?.name}
                    transactions={transactions}
                  />
                )}
                {newDetailType === "measurable" && (
                  <TextField
                    label="Unit of Measure"
                    value={newDetailUnit}
                    onChange={(e) => setNewDetailUnit(e.target.value)}
                    fullWidth
                    disabled={savingDetails}
                    placeholder="e.g., km, L, kg, m"
                    helperText="Enter the unit for this measurable detail (e.g., km for distance, L for volume)"
                  />
                )}
                <Stack direction="row" spacing={1}>
                  {(editingDetailIndex !== null || hasPartialFormData(newDetailName, newDetailType, newDetailUnit, newListItems, editingDetailIndex)) && (
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setNewDetailName("");
                        setNewDetailType("");
                        setNewDetailUnit("");
                        setNewListItems([]);
                        setDetailNameError("");
                        setEditingDetailIndex(null);
                        setEditingOriginalDetail(null);
                      }}
                      sx={{ flex: 1 }}
                      disabled={savingDetails}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    onClick={() => {
                      if (!newDetailName.trim()) {
                        setDetailNameError("Detail name is required");
                        return;
                      }

                      if (!newDetailType) {
                        setDetailNameError("Detail type is required");
                        return;
                      }

                      if (newDetailType === "measurable" && !newDetailUnit.trim()) {
                        setDetailNameError("Unit is required for measurable details");
                        return;
                      }

                      if (newDetailType === "list" && newListItems.length === 0) {
                        setDetailNameError("At least one list item is required");
                        return;
                      }

                      // Check for duplicate names (excluding current editing item)
                      if (editingDetailIndex !== null) {
                        const nameConflict = editingDetails.some(
                          (d, i) => i !== editingDetailIndex && d.name.toLowerCase() === newDetailName.trim().toLowerCase()
                        );
                        if (nameConflict) {
                          setDetailNameError("A detail with this name already exists.");
                          return;
                        }
                      } else {
                        if (editingDetails.some((d) => d.name.toLowerCase() === newDetailName.trim().toLowerCase())) {
                          setDetailNameError("A detail with this name already exists.");
                          return;
                        }
                      }

                      const newDetail: TransactionCategoryDetail = {
                        name: newDetailName.trim(),
                        type: newDetailType,
                        ...(newDetailType === "list" ? { listItems: [...newListItems] } : {}),
                        ...(newDetailType === "measurable" ? { unit: newDetailUnit.trim() } : {}),
                      };

                      if (editingDetailIndex !== null) {
                        // Update existing detail
                        const updatedDetails = [...editingDetails];
                        updatedDetails[editingDetailIndex] = newDetail;
                        setEditingDetails(updatedDetails);
                      } else {
                        // Add new detail
                        setEditingDetails([...editingDetails, newDetail]);
                      }
                      
                      // Clear form
                      setNewDetailName("");
                      setNewDetailType("");
                      setNewDetailUnit("");
                      setNewListItems([]);
                      setDetailNameError("");
                      setEditingDetailIndex(null);
                      setEditingOriginalDetail(null);
                    }}
                    disabled={!newDetailName.trim() || !newDetailType || savingDetails}
                    sx={{ flex: 1 }}
                  >
                    {editingDetailIndex !== null ? "Update Detail" : "Add Detail"}
                  </Button>
                </Stack>
              </Stack>
            </Box>

            {/* Existing Details List - Below Add Detail section */}
            {editingDetails.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Existing Details
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: editingDetails.length >= 3 ? "240px" : "none",
                      overflowY: editingDetails.length >= 3 ? "auto" : "visible",
                      overflowX: "hidden",
                      pr: editingDetails.length >= 3 ? 1 : 0,
                    }}
                  >
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={detailHandlers.handleDetailDragEnd}
                    >
                      <SortableContext
                        items={editingDetails.map((_, index) => String(index))}
                        strategy={verticalListSortingStrategy}
                      >
                        <Stack spacing={1}>
                          {editingDetails.map((detail, index) => {
                            const categoryName = categoryForDetails?.name || "";
                            const detailUsed = isDetailUsed(categoryName, detail.name);
                            return (
                              <SortableDetailCard
                                key={index}
                                detail={detail}
                                index={index}
                                categoryName={categoryName}
                                isDetailUsed={detailUsed}
                                onEditDetail={detailHandlers.handleEditDetail}
                                onRemoveDetail={detailHandlers.handleRemoveDetail}
                                editingDetailIndex={editingDetailIndex}
                              />
                            );
                          })}
                        </Stack>
                      </SortableContext>
                    </DndContext>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </StandardDialog>
      )}

      {/* Select Details Dialog - Choose which details to enable for category */}
      {categoryForSelectDetails && (
        <SelectDetailsDialog
          open={selectDetailsDialogOpen}
          onClose={() => {
            if (!savingDetails) {
              setSelectDetailsDialogOpen(false);
              setCategoryForSelectDetails(null);
              setEnabledDetails([]);
              setDefaultValues({});
              setOriginalEnabledDetails([]);
              setOriginalDefaultValues({});
            }
          }}
          onConfirm={detailHandlers.handleSaveSelectDetails}
          category={categoryForSelectDetails}
          groups={groups}
          enabledDetails={enabledDetails}
          setEnabledDetails={setEnabledDetails}
          defaultValues={defaultValues}
          setDefaultValues={setDefaultValues}
          originalEnabledDetails={originalEnabledDetails}
          originalDefaultValues={originalDefaultValues}
          savingDetails={savingDetails}
          error={error}
          isDetailUsed={isDetailUsed}
          hasDetailSelectionChanged={hasDetailSelectionChanged}
        />
      )}

      {/* Group Details Management Dialog */}
      <GroupDetailsDialog
        open={groupDetailsDialogOpen}
        onClose={() => {
          if (!savingDetails) {
            setGroupDetailsDialogOpen(false);
            setEditingGroupDetails([]);
            setOriginalGroupDetails([]);
            setEditingGroupNameForDetails("");
            setNewDetailName("");
            setNewDetailType("");
            setNewDetailUnit("");
            setNewListItems([]);
            setDetailNameError("");
            setEditingDetailIndex(null);
            setEditingOriginalDetail(null);
          }
        }}
        onConfirm={detailHandlers.handleSaveGroupDetails}
                  groupName={editingGroupNameForDetails}
        editingGroupDetails={editingGroupDetails}
        setEditingGroupDetails={setEditingGroupDetails}
        originalGroupDetails={originalGroupDetails}
        newDetailName={newDetailName}
        setNewDetailName={setNewDetailName}
        newDetailType={newDetailType}
        setNewDetailType={setNewDetailType}
        newDetailUnit={newDetailUnit}
        setNewDetailUnit={setNewDetailUnit}
        newListItems={newListItems}
        setNewListItems={setNewListItems}
        detailNameError={detailNameError}
        setDetailNameError={setDetailNameError}
        editingDetailIndex={editingDetailIndex}
        setEditingDetailIndex={setEditingDetailIndex}
        editingOriginalDetail={editingOriginalDetail}
        setEditingOriginalDetail={setEditingOriginalDetail}
        savingDetails={savingDetails}
        error={error}
        categories={categories}
        groups={groups}
                  transactions={transactions}
        isGroupDetailUsed={isGroupDetailUsed}
        hasGroupDetailsChanged={hasGroupDetailsChanged}
        hasPartialFormData={hasPartialFormData}
        hasEditedDetailChanged={hasEditedDetailChanged}
        onDragEnd={detailHandlers.handleGroupDetailDragEnd}
        onEditDetail={detailHandlers.handleEditGroupDetail}
        onRemoveDetail={detailHandlers.handleRemoveGroupDetail}
      />

      {/* Delete Confirmation Dialog */}
      {categoryToDelete && (
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={categoryHandlers.handleConfirmDelete}
          itemName={categoryToDelete.name}
          itemType="category"
          title="Delete Category"
          disabled={saving}
        />
      )}

      {/* Delete Group Confirmation Dialog */}
      {groupToDelete && (
        <DeleteConfirmationDialog
          open={groupDeleteDialogOpen}
          onClose={() => setGroupDeleteDialogOpen(false)}
          onConfirm={groupHandlers.handleConfirmDeleteGroup}
          itemName={groupToDelete.name}
          itemType="group"
          title="Delete Group"
          disabled={saving}
        />
      )}

      {/* Add/Edit Group Dialog */}
      <CategoryGroupDialog
        open={addGroupDialogOpen || editGroupDialogOpen}
        onClose={() => {
          if (!creatingGroup && !saving) {
            if (addGroupDialogOpen) {
              setAddGroupDialogOpen(false);
              setNewGroupName("");
              setGroupNameError("");
            }
            if (editGroupDialogOpen) {
              setEditGroupDialogOpen(false);
            }
          }
        }}
        onConfirm={async () => {
          if (editGroupDialogOpen && editingGroup) {
            await groupHandlers.handleSaveGroupEdit();
          } else if (addGroupDialogOpen) {
          if (!newGroupName.trim() || creatingGroup || groupNameError) return;
            await groupHandlers.handleCreateGroup(newGroupName.trim());
          setAddGroupDialogOpen(false);
          setNewGroupName("");
          setGroupNameError("");
          }
        }}
        selectedTab={selectedTab}
        editingGroup={editingGroup}
        groups={groups}
        groupName={editGroupDialogOpen ? editGroupName : newGroupName}
        setGroupName={(name) => {
          if (editGroupDialogOpen) {
            setEditGroupName(name);
          } else {
            setNewGroupName(name);
            // Check for duplicate group name
            const trimmedName = name.trim();
            if (trimmedName) {
              const duplicateGroup = groups.find(
                (g) => g.name.toLowerCase() === trimmedName.toLowerCase() && g.type === selectedTab
              );
              if (duplicateGroup) {
                setGroupNameError(`Group name already exists for ${selectedTab === "expense" ? "expense" : "income"} type`);
              } else {
                setGroupNameError("");
              }
            } else {
              setGroupNameError("");
            }
          }
        }}
        groupNameError={groupNameError}
        error={error}
        saving={saving}
        creatingGroup={creatingGroup}
        countCategoriesInGroup={countCategoriesInGroup}
        countTransactionsInGroup={countTransactionsInGroup}
      />
    </Box>
  );
}

export default CategorySettings;
